import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Divider, Stack, Typography, Paper, MenuItem, Button, Slide } from '@mui/material';
import LoaderSpinner from '../../SystemDesign/LoaderSpinner';
import PaginationUI from '../../SystemDesign/PaginationUI';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MuiFullScreenDialog from '../../SystemDesign/MuiFullScreenDialog';
import CardMenuActions from '../../SystemDesign/CardMenuActions';
import DeleteQuestion from './DeleteQuestion';
import UpdateOption from '../QuizOptions/UpdateOption';
// SeeMoreQuestion dialog kept for future use, but right-panel view is implemented inline here
import { useGetQuizQuestionsQuery, useGetRequirementsByPosteQuery } from '../../services/SkillsManagement';
import CreateQuestion from './CreateQuestion';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../Reducers/Actions';

// Full-screen manager for questions of a quiz
const QuestionList = ({ open, onClose, quizId, quizTitle, posteId }) => {
  const dispatch = useDispatch();
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showRight, setShowRight] = useState(false);
  const [viewQuestion, setViewQuestion] = useState(null);
  const { data, isLoading, isFetching, refetch } = useGetQuizQuestionsQuery(
    { id: Number(quizId) },
    {
      skip: !quizId,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );

  // Keep RTK Query as the source of truth to initialize, then read from Redux so local deletes reflect instantly
  const allQuestions = useSelector((state) => state.quizQuestions);
  const numericQuizId = Number(quizId);
  const questions = useMemo(() => {
    return Array.isArray(allQuestions)
      ? allQuestions.filter((q) => Number(q?.quizId) === numericQuizId)
      : [];
  }, [allQuestions, numericQuizId]);

  // Pagination state (client-side), similar to SkillsList
  const [paginationInfo, setPaginationInfo] = useState(() => {
    const savedRowsPerPage = localStorage.getItem('quiz-questions-rowsPerPage');
    const rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;
    return { currentPage: 1, rowsPerPage, total: 0 };
  });

  // Keep total in sync with questions length (for this quiz only)
  useEffect(() => {
    const total = Array.isArray(questions) ? questions.length : 0;
    setPaginationInfo((prev) => (prev.total === total ? prev : { ...prev, total }));
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[QuestionList] questions changed:', { total, sample: (questions || []).slice(0, 3).map(q => ({ id: q.questionId, order: q.order })) });
    }
  }, [questions]);

  // Sort questions by "order" or "questionOrder" to respect backend-provided ordering
  const sortedQuestions = useMemo(() => {
    return (questions || [])
      .slice()
      .sort(
        (a, b) => (a.order ?? a.questionOrder ?? 0) - (b.order ?? b.questionOrder ?? 0)
      );
  }, [questions]);

  // Compute paged questions from the sorted list, then group
  const pagedQuestions = useMemo(() => {
    const total = Array.isArray(sortedQuestions) ? sortedQuestions.length : 0;
    const totalPages = Math.max(1, Math.ceil(total / paginationInfo.rowsPerPage));
    const safePage = Math.min(Math.max(1, paginationInfo.currentPage), totalPages);
    const start = (safePage - 1) * paginationInfo.rowsPerPage;
    const end = start + paginationInfo.rowsPerPage;
    return (sortedQuestions || []).slice(start, end);
  }, [sortedQuestions, paginationInfo.currentPage, paginationInfo.rowsPerPage]);

  // Load requirements to resolve skill names
  const { data: reqData } = useGetRequirementsByPosteQuery(
    {
      posteId: Number(posteId),
      pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 1000 }),
      sorting: '',
    },
    { skip: !posteId }
  );
  const skillNameById = useMemo(() => {
    const arr = Array.isArray(reqData?.requirements || reqData?.data?.requirements)
      ? (reqData?.requirements || reqData?.data?.requirements)
      : [];
    const map = new Map();
    arr.forEach((r, idx) => {
      const id = Number(r?.skill_id ?? r?.skillId ?? idx);
      const name = r?.skill?.name || r?.skill_name || `Skill ${id}`;
      if (Number.isFinite(id)) map.set(id, name);
    });
    return map;
  }, [reqData]);

  const grouped = useMemo(() => {
    const g = new Map();
    (pagedQuestions || []).forEach((q) => {
      const sid = Number(q?.skillId ?? q?.skill?.skillId ?? q?.skill_id ?? -1);
      const key = Number.isFinite(sid) ? sid : -1;
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(q);
    });
    // Ensure each group's questions are also sorted by order
    for (const [k, arr] of g.entries()) {
      g.set(
        k,
        arr.slice().sort((a, b) => (a.order ?? a.questionOrder ?? 0) - (b.order ?? b.questionOrder ?? 0))
      );
    }
    return g;
  }, [pagedQuestions]);

  // Initialize questions from RTK Query only when local slice is empty
  const initDoneRef = useRef(false);
  // On first open of same quiz: refetch without clearing; when switching quiz: clear then refetch
  const prevOpenRef = useRef(open);
  const prevQuizIdRef = useRef(quizId);
  useEffect(() => {
    const openingNow = !prevOpenRef.current && open;
    prevOpenRef.current = open;
    const quizChanged = prevQuizIdRef.current !== quizId;
    prevQuizIdRef.current = quizId;
    if (!open || !quizId) return;
    if (quizChanged) {
      dispatch({ type: actions.CLEAR_QUIZ_QUESTIONS });
      initDoneRef.current = false;
      try { refetch?.(); } catch {}
    } else if (openingNow) {
      try { refetch?.(); } catch {}
    }
  }, [dispatch, quizId, open, refetch]);
  useEffect(() => {
    const fetched = data?.data?.questions || data?.questions || [];
    const stateEmpty = !Array.isArray(questions) || questions.length === 0;
    if (stateEmpty && !initDoneRef.current && Array.isArray(fetched) && fetched.length > 0) {
      // Stamp questions with the current quizId so the reducer can normalize and we can filter by quiz reliably
      const withQuizId = fetched.map((q) => ({ ...q, quizId: Number(quizId) }));
      dispatch({ type: actions.INITIALIZE_QUIZ_QUESTIONS, payload: withQuizId });
      initDoneRef.current = true;
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[QuestionList] INITIALIZE from RTK data (state was empty)', { count: fetched.length, quizId });
      }
    }
  }, [dispatch, data, quizId]);

  // Options are fetched per-question in the QuestionCard component now.

  const content = (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: showRight ? '1fr 1fr' : '1fr' }, gap: 3 }}>
      {/* Left column: questions list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle1">Existing Questions ({questions.length})</Typography>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedQuestion(null); setViewQuestion(null); setShowRight(true); }}>
              Add Question
            </Button>
          </Stack>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ overflow: 'auto', maxHeight: '65vh', pr: 1 }}>
          {questions.length === 0 && !isLoading && (
            <Typography variant="body2" color="text.secondary">No questions yet. Use the form to add one.</Typography>
          )}
          <Stack spacing={2}>
            {[...grouped.entries()].map(([sid, items]) => (
              <Paper key={sid} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">
                    {sid === -1 ? 'Unassigned Skill' : (skillNameById.get(Number(sid)) || `Skill ${sid}`)}
                  </Typography>
                  <Chip size="small" label={`${items.length} question${items.length > 1 ? 's' : ''}`} variant="outlined" />
                </Stack>
                <Stack spacing={1.25}>
                  {items.map((q, idx) => (
                    <QuestionCard
                      key={(q.questionId || q.id || idx) + '-q'}
                      question={q}
                      index={idx}
                      onEdit={(q) => { setSelectedQuestion(q); setViewQuestion(null); setShowRight(true); }}
                      onView={(q) => { setSelectedQuestion(null); setViewQuestion(q); setShowRight(true); }}
                      skillNameById={skillNameById}
                    />
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>

        {/* Pagination */}
        <PaginationUI
          paginationInfo={paginationInfo}
          isLoading={isLoading || isFetching}
          onPageChange={(page, rowsPerPage) => setPaginationInfo((prev) => ({ ...prev, currentPage: page, rowsPerPage }))}
          onRowsPerPageChange={(rows) => {
            localStorage.setItem('quiz-questions-rowsPerPage', rows.toString());
            setPaginationInfo((prev) => ({ ...prev, currentPage: 1, rowsPerPage: rows }));
          }}
          pageSizeOptions={[5, 10, 25, 50]}
        />

        {/* Per-question options are shown within each QuestionCard */}
      </Box>

      {/* Right column: slide-in create/edit panel */}
      <Slide direction="left" in={showRight} mountOnEnter unmountOnExit>
        <Box sx={{ display: 'grid', gap: 3 }}>
          {viewQuestion ? (
            <ViewQuestionPanel
              question={viewQuestion}
              skillNameById={skillNameById}
              onClose={() => { setViewQuestion(null); setShowRight(false); }}
            />
          ) : selectedQuestion ? (
            <UpdateOption
              question={selectedQuestion}
              posteId={posteId}
              onUpdated={() => {/* reducer updates state; keep selection */}}
              onCancel={() => { setSelectedQuestion(null); setShowRight(false); }}
            />
          ) : (
            <CreateQuestion
              quizId={quizId}
              posteId={posteId}
              onCreated={() => {
                refetch();
              }}
              onCancel={() => { setShowRight(false); }}
            />
          )}

          {/* Removed duplicate Add Option form (handled in CreateQuestion) */}
        </Box>
      </Slide>
    </Box>
  );

  // Debug: track open prop transitions
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[QuestionList] open prop changed:', { open });
    }
  }, [open]);

  return (
    <>
      <LoaderSpinner loading={isLoading || isFetching} />
      <MuiFullScreenDialog
      open={open}
      onClose={(e, reason) => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[QuestionList] onClose called with reason=', reason);
        }
        onClose?.(e, reason);
      }}
      title={`Questions Management - ${quizTitle || ''}`}
      subtitle="Add and manage questions for this quiz"
      content={content}
      maxWidth="xl"
      allowBackdropClose={false}
      allowEscapeClose={false}
      showCloseButton={true}
    />
    </>
  );
};

// Renders a single question as a card with inline options
const QuestionCard = ({ question, index, onEdit, onView, onDeleted, skillNameById }) => {
  const options = Array.isArray(question?.options)
    ? question.options.slice().sort((a, b) => (a.order ?? a.optionOrder ?? 0) - (b.order ?? b.optionOrder ?? 0))
    : [];

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip size="small" label={`Q${index + 1}`} variant="outlined" />
          <Chip size="small" label={(question.questionType || '').toString()} color="default" variant="outlined" />
          <Chip size="small" label={`${question.points ?? 0} pts`} variant="outlined" />
          {question.isRequired ? <Chip size="small" label="Required" color="primary" /> : null}
        </Stack>
        <CardMenuActions>
          <MenuItem onClick={() => onView?.(question)}>
            <VisibilityIcon fontSize="small" style={{ marginRight: 8 }} />
            View
          </MenuItem>
          <MenuItem onClick={() => onEdit?.(question)}>
            <EditOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
            Edit
          </MenuItem>
          <DeleteQuestion question={question} onDeleted={onDeleted} />
        </CardMenuActions>
      </Stack>

      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{question.questionText}</Typography>

      {question.hint ? (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
          <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">{question.hint}</Typography>
        </Stack>
      ) : null}

      {/* Options */}
      <Box>
        {options.length === 0 ? (
          <Typography variant="caption" color="text.secondary">No options yet for this question.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {options.map((opt) => (
              <Stack key={opt.optionId} direction="row" alignItems="center" spacing={1}>
                {opt.isCorrect ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                )}
                <Typography variant="body2" sx={{
                  fontWeight: opt.isCorrect ? 600 : 400,
                  color: opt.isCorrect ? 'success.main' : 'text.secondary'
                }}>
                  {opt.optionText}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

// Inline right-panel: view question details with options
const ViewQuestionPanel = ({ question, skillNameById, onClose }) => {
  const options = Array.isArray(question?.options)
    ? question.options.slice().sort((a, b) => (a.order ?? a.optionOrder ?? 0) - (b.order ?? b.optionOrder ?? 0))
    : [];

  const skillName = skillNameById?.get?.(Number(question?.skillId ?? question?.skill_id));

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Question Details</Typography>
        <Button size="small" variant="text" onClick={onClose}>Cancel</Button>
      </Stack>
      <Stack spacing={1.25}>
        <Typography variant="subtitle2">{question.questionText}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={String(question.questionType)} variant="outlined" />
          <Chip size="small" label={`${question.points ?? 0} pts`} variant="outlined" />
          <Chip size="small" label={`Order ${question.questionOrder ?? question.order ?? 0}`} variant="outlined" />
          <Chip size="small" label={question.isRequired ? 'Required' : 'Optional'} color={question.isRequired ? 'primary' : 'default'} />
        </Stack>
        <Typography variant="body2"><b>Skill:</b> {skillName || '-'}</Typography>
        {question.timeLimit != null && <Typography variant="body2"><b>Time Limit:</b> {String(question.timeLimit)}</Typography>}
        {question.hint && <Typography variant="body2"><b>Hint:</b> {question.hint}</Typography>}
        {question.explanation && <Typography variant="body2"><b>Explanation:</b> {question.explanation}</Typography>}
        <Divider />
        <Typography variant="subtitle2">Answer Options</Typography>
        {options.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No options</Typography>
        ) : (
          <Stack spacing={0.5}>
            {options.map((o) => (
              <Stack key={o.optionId} direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={o.isCorrect ? 'Correct' : 'Incorrect'} color={o.isCorrect ? 'success' : 'default'} variant={o.isCorrect ? 'filled' : 'outlined'} />
                <Typography variant="body2">{o.optionText}</Typography>
                <Chip size="small" variant="outlined" label={`Order ${o.order ?? 0}`} />
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

QuestionList.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  quizId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  quizTitle: PropTypes.string,
  posteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default QuestionList;
